import { useEffect, useState } from 'react';
import { Badge, Box, Button, HStack, NativeSelect, Spinner, Stack, Text } from '@chakra-ui/react';
import { Navigate, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import AuthHeader from '@/components/auth-header';
import { Checkbox } from '@/components/ui/checkbox';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/lib/hooks/use-auth';
import { OAuthConsentDetails } from '@/lib/types';
import { decideOAuthAuthorization, getOAuthAuthorizationRequest } from '@/utils/oauth-connections';
import { clearReturnTo, rememberReturnTo } from '@/utils/return-to';

// Errors the API redirects here with when a request cannot even reach consent
// (unknown client, unregistered redirect). Descriptions are ours, never the
// client's, so they are safe to show.
const errorMessages: Record<string, string> = {
  invalid_client: 'The app that sent you here could not be identified.',
  invalid_request: 'The connection request was invalid. Go back to the app and try again.',
  temporarily_unavailable:
    'The connection service is temporarily unavailable. Please try again in a moment.',
  server_error: 'Something went wrong on our side. Please try again.',
};

type State = 'loading' | 'ready' | 'submitting' | 'done' | 'expired' | 'inactive' | 'failed';

// OAuthConsent is the consent screen for assistants (Claude, ChatGPT, …)
// connecting through the MCP server. The API parks the authorization request
// and redirects the browser here; approving mints the code and we hand the
// browser back to the app's redirect URI.
const OAuthConsent = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const auth = useAuth();
  const requestId = params.get('request') ?? '';
  const errorCode = params.get('error');

  const [details, setDetails] = useState<OAuthConsentDetails | null>(null);
  const [state, setState] = useState<State>('loading');
  const [workspaceId, setWorkspaceId] = useState<number>(0);
  // Write scopes the user unticked. Read scopes are always granted.
  const [declined, setDeclined] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!auth.isAuthenticated || errorCode) return;
    // We are the return-to destination; stop the stored path from steering
    // later logins in this tab.
    clearReturnTo();
    if (!requestId) {
      setState('failed');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await getOAuthAuthorizationRequest(requestId);
        if (cancelled) return;
        setDetails(d);
        const preferred = d.workspaces.find((w) => w.is_default) ?? d.workspaces[0];
        setWorkspaceId(preferred?.id ?? 0);
        setState('ready');
      } catch (e: any) {
        if (cancelled) return;
        const status = e?.response?.status;
        setState(
          status === 404 || status === 410 ? 'expired' : status === 403 ? 'inactive' : 'failed',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestId, auth.isAuthenticated, errorCode]);

  if (!auth.isAuthenticated) {
    // Come back here after login; the request stays valid for ten minutes.
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to='/auth/login' replace />;
  }

  const toggleScope = (scope: string, checked: boolean) =>
    setDeclined((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(scope);
      else next.add(scope);
      return next;
    });

  const decide = async (decision: 'approve' | 'deny') => {
    setState('submitting');
    // Send a scope list only when something was unticked: the API then
    // replaces the grant's scopes, whereas omitting it merges with what an
    // earlier consent already granted.
    const chosen = details?.scopes.map((s) => s.scope).filter((s) => !declined.has(s));
    try {
      const { redirect_to } = await decideOAuthAuthorization(requestId, {
        decision,
        workspace_id: decision === 'approve' ? workspaceId : undefined,
        scopes: decision === 'approve' && declined.size > 0 ? chosen : undefined,
      });
      setState('done');
      window.location.assign(redirect_to);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404 || status === 410) {
        setState('expired');
        return;
      }
      setState('ready');
      toaster.create({
        type: 'error',
        title: e?.response?.data?.error || 'Could not complete the request. Please try again.',
      });
    }
  };

  if (errorCode || state === 'failed' || state === 'expired' || state === 'inactive') {
    const message =
      state === 'expired'
        ? 'This connection request has expired. Go back to the app and connect again.'
        : state === 'inactive'
          ? 'Your account is not active, so it cannot be connected to other apps.'
          : errorMessages[errorCode ?? ''] || errorMessages.invalid_request;
    return (
      <Stack gap={6}>
        <AuthHeader title='Connection failed' />
        <Text textAlign='center' color='fg.muted'>
          {message}
        </Text>
        <Box textAlign='center'>
          <Button variant='outline' asChild>
            <NavLink to='/'>Go to Athlete Era</NavLink>
          </Button>
        </Box>
      </Stack>
    );
  }

  if (state === 'loading' || !details) {
    return (
      <Stack gap={6} align='center'>
        <AuthHeader title='Connect to Athlete Era' />
        <Spinner size='lg' />
      </Stack>
    );
  }

  if (state === 'done') {
    return (
      <Stack gap={6} align='center'>
        <AuthHeader title='Returning to the app…' />
        <Spinner size='lg' />
      </Stack>
    );
  }

  const { client, scopes, workspaces } = details;
  const busy = state === 'submitting';
  const canApprove =
    workspaces.length > 0 && workspaceId > 0 && scopes.some((s) => !declined.has(s.scope));

  return (
    <Stack gap={6}>
      <AuthHeader title='Connect to Athlete Era' />

      <Box borderWidth={1} borderColor='border' borderRadius='lg' p={5} bg='bg.panel'>
        <Stack gap={2}>
          <HStack justify='space-between' align='start'>
            <Box>
              <Text fontSize='lg' fontWeight='600' color='fg.strong'>
                {client.host}
              </Text>
              {!client.verified && client.name && (
                <Text fontSize='sm' color='fg.muted'>
                  Calls itself “{client.name}”
                </Text>
              )}
            </Box>
            {client.verified ? (
              <Badge colorPalette='green'>Verified</Badge>
            ) : (
              <Badge colorPalette='orange'>Unverified app</Badge>
            )}
          </HStack>
          <Text color='fg.muted'>wants to access your Athlete Era account.</Text>
        </Stack>
      </Box>

      <Stack gap={2}>
        <Text fontWeight='600' color='fg.strong'>
          It will be able to:
        </Text>
        <Stack as='ul' gap={2} pl={0} listStyleType='none'>
          {scopes.map((s) => (
            <HStack as='li' key={s.scope} align='start' gap={2}>
              {s.write ? (
                <Checkbox
                  flex={1}
                  colorPalette='brand'
                  checked={!declined.has(s.scope)}
                  onCheckedChange={({ checked }) => toggleScope(s.scope, !!checked)}
                  disabled={busy}
                >
                  {/* Match the read-scope bullets (<p> gets the DS body role globally). */}
                  <Text as='span' textStyle='body'>
                    {s.description || s.scope}
                  </Text>
                </Checkbox>
              ) : (
                <>
                  <Text color='fg.muted'>•</Text>
                  <Text flex={1}>{s.description || s.scope}</Text>
                </>
              )}
              {s.write && <Badge colorPalette='orange'>Makes changes</Badge>}
            </HStack>
          ))}
        </Stack>
        {scopes.some((s) => s.write) && (
          <Text fontSize='sm' color='fg.muted'>
            Untick anything that makes changes if you only want the app to read.
          </Text>
        )}
      </Stack>

      <Stack gap={1}>
        <Text fontWeight='600' color='fg.strong'>
          Workspace to use by default
        </Text>
        {workspaces.length === 0 ? (
          <Text color='fg.muted'>
            You are not a member of any workspace yet, so nothing can be shared.
          </Text>
        ) : (
          <NativeSelect.Root disabled={busy}>
            <NativeSelect.Field
              value={String(workspaceId)}
              onChange={(e) => setWorkspaceId(Number(e.target.value))}
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.sport_type ? ` (${w.sport_type})` : ''} — {w.role_name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        )}
        <Text fontSize='sm' color='fg.muted'>
          The app can still ask for your other workspaces by name. Your role in each workspace
          applies.
        </Text>
      </Stack>

      <HStack gap={3}>
        <Button variant='outline' flex={1} onClick={() => decide('deny')} disabled={busy}>
          Deny
        </Button>
        <Button
          colorPalette='brand'
          flex={1}
          onClick={() => decide('approve')}
          loading={busy}
          loadingText='Connecting…'
          disabled={!canApprove}
        >
          Allow
        </Button>
      </HStack>

      <Text fontSize='sm' color='fg.muted' textAlign='center'>
        You can disconnect at any time from Settings → Connected apps.
      </Text>
    </Stack>
  );
};

export default OAuthConsent;
